import React from "react";
import { Document, Page, View, Text, Image, StyleSheet } from "@react-pdf/renderer";

const BRAND = "#9f0059";
const DARK  = "#1a1a1a";
const GREY  = "#555555";

const fmtMoney = (n, moneda) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = parseFloat(n) || 0;
  return (moneda === "USD" ? "USD " : "$") + num.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtFecha = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const styles = StyleSheet.create({
  page: { fontFamily: "Helvetica", fontSize: 9, color: DARK, paddingBottom: 44 },
  headerBand: { backgroundColor: "#d0ced0", padding: "10 28", flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  headerLogo: { width: 70, height: 50, objectFit: "contain" },
  headerCenter: { flex: 1, alignItems: "center" },
  headerTitle: { color: DARK, fontSize: 14, fontFamily: "Helvetica-Bold", letterSpacing: 1 },
  headerSub: { color: GREY, fontSize: 8.5, marginTop: 3 },
  headerSpacer: { width: 70 },
  body: { paddingHorizontal: 28, paddingTop: 16 },

  // Cabecera de cada orden
  ordenHead: {
    flexDirection: "row", justifyContent: "space-between", alignItems: "center",
    backgroundColor: "#2d2d2d", padding: "6 10", marginTop: 12, borderRadius: 3,
  },
  ordenHeadLeft: { color: "#fff", fontSize: 10, fontFamily: "Helvetica-Bold" },
  ordenHeadRight: { color: "#fff", fontSize: 9 },
  ordenMeta: { flexDirection: "row", justifyContent: "space-between", padding: "4 10", backgroundColor: "#f5f0f3" },
  ordenMetaText: { fontSize: 8.5, color: GREY },

  itemsHeader: { flexDirection: "row", padding: "4 10", borderBottom: "1 solid #ddd", marginTop: 2 },
  itemsHeaderText: { fontSize: 7.5, color: GREY, fontFamily: "Helvetica-Bold", textTransform: "uppercase" },
  itemRow: (even) => ({ flexDirection: "row", padding: "4 10", backgroundColor: even ? "#fafafa" : "#fff", borderBottom: "0.5 solid #eee" }),
  itemText: { fontSize: 8.5, color: DARK },
  colDesc: { flex: 4 },
  colCant: { flex: 1, textAlign: "center" },
  colPrec: { flex: 1.5, textAlign: "right" },
  sinItems: { fontSize: 8, color: "#aaa", fontFamily: "Helvetica-Oblique", padding: "5 10" },

  emptyBox: { marginTop: 30, padding: "20 24", backgroundColor: "#f5f0f3", borderRadius: 6, alignItems: "center" },
  emptyText: { fontSize: 11, color: GREY },

  footer: { position: "absolute", bottom: 16, left: 28, right: 28, flexDirection: "row", justifyContent: "space-between", borderTop: "1 solid #ede8ed", paddingTop: 6 },
  footerText: { fontSize: 8, color: "#aaa" },
});

const ReporteClientePDF = ({ cliente, meses, ordenes = [], logoUrl }) => {
  const totalProductos = ordenes.reduce((s, o) => s + (o.items?.length || 0), 0);
  const sub = `Generado el ${fmtFecha(new Date())} · últimos ${meses} meses · ${ordenes.length} orden${ordenes.length !== 1 ? "es" : ""} · ${totalProductos} producto${totalProductos !== 1 ? "s" : ""}`;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerBand} fixed>
          {logoUrl ? <Image src={logoUrl} style={styles.headerLogo} /> : <View style={styles.headerSpacer} />}
          <View style={styles.headerCenter}>
            <Text style={styles.headerTitle}>DETALLE DE CLIENTE</Text>
            <Text style={styles.headerSub}>{cliente || "—"}</Text>
            <Text style={styles.headerSub}>{sub}</Text>
          </View>
          <View style={styles.headerSpacer} />
        </View>

        <View style={styles.body}>
          {ordenes.length === 0 ? (
            <View style={styles.emptyBox}>
              <Text style={styles.emptyText}>Sin órdenes en el período</Text>
            </View>
          ) : (
            ordenes.map((o, idx) => (
              <View key={idx} wrap={false}>
                {/* Cabecera de la orden */}
                <View style={styles.ordenHead}>
                  <Text style={styles.ordenHeadLeft}>Orden #{o.id}</Text>
                  <Text style={styles.ordenHeadRight}>{fmtMoney(o.monto_total, o.moneda)}</Text>
                </View>
                <View style={styles.ordenMeta}>
                  <Text style={styles.ordenMetaText}>Creada: {fmtFecha(o.created_at)}</Text>
                  <Text style={styles.ordenMetaText}>Estado: {o.estado_actual || "—"}</Text>
                  <Text style={styles.ordenMetaText}>Entrega: {fmtFecha(o.fecha_entrega)}</Text>
                </View>
                {/* Ítems */}
                {(o.items || []).length === 0 ? (
                  <Text style={styles.sinItems}>Sin productos cargados</Text>
                ) : (
                  <>
                    <View style={styles.itemsHeader}>
                      <Text style={[styles.itemsHeaderText, styles.colDesc]}>Producto</Text>
                      <Text style={[styles.itemsHeaderText, styles.colCant]}>Cant.</Text>
                      <Text style={[styles.itemsHeaderText, styles.colPrec]}>P. Unit.</Text>
                    </View>
                    {o.items.map((it, j) => (
                      <View key={j} style={styles.itemRow(j % 2 === 0)}>
                        <Text style={[styles.itemText, styles.colDesc]}>{it.descripcion || "—"}</Text>
                        <Text style={[styles.itemText, styles.colCant]}>{it.cantidad}</Text>
                        <Text style={[styles.itemText, styles.colPrec]}>{fmtMoney(it.precio, o.moneda)}</Text>
                      </View>
                    ))}
                  </>
                )}
              </View>
            ))
          )}
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Twinpack — Detalle de {cliente || "cliente"}</Text>
          <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default ReporteClientePDF;
